import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export async function initializeFirestore(): Promise<void> {
  try {
    console.log('Starting Firestore initialization...');

    // Check if data already exists
    const categoriesSnap = await getDocs(
      query(collection(db, 'categories'), limit(1))
    );
    if (categoriesSnap.size > 0) {
      console.log('Firestore already initialized with data');
      return;
    }

    // Initialize Categories
    console.log('Creating categories...');
    const categories = [
      { name: 'Vinyl', parentCategoryId: null, order: 1 },
      { name: 'Acrylic', parentCategoryId: null, order: 2 },
      { name: 'Wood', parentCategoryId: null, order: 3 },
      { name: 'Metal', parentCategoryId: null, order: 4 },
      { name: 'Glass', parentCategoryId: null, order: 5 },
    ];

    const categoryRefs = await Promise.all(
      categories.map(async (cat, index) => {
        const catRef = doc(collection(db, 'categories'));
        await setDoc(catRef, {
          ...cat,
          createdAt: Timestamp.now(),
        });
        return catRef.id;
      })
    );

    // Initialize Materials
    console.log('Creating materials...');
    const materials = [
      {
        name: 'White Vinyl',
        categoryId: categoryRefs[0],
        thickness: '3.2mil',
        size: '54" x 100yds',
        color: 'White',
        weight: 25,
        basePrice: 45.0,
        markupFactor: 2.0,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Clear Vinyl',
        categoryId: categoryRefs[0],
        thickness: '3.2mil',
        size: '54" x 100yds',
        color: 'Clear',
        weight: 25,
        basePrice: 50.0,
        markupFactor: 2.0,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Chrome Silver',
        categoryId: categoryRefs[0],
        thickness: '3.2mil',
        size: '54" x 100yds',
        color: 'Silver',
        weight: 27,
        basePrice: 65.0,
        markupFactor: 2.2,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Black Vinyl',
        categoryId: categoryRefs[0],
        thickness: '3.2mil',
        size: '54" x 100yds',
        color: 'Black',
        weight: 25,
        basePrice: 48.0,
        markupFactor: 2.0,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Clear Acrylic',
        categoryId: categoryRefs[1],
        thickness: '1/8"',
        size: '48" x 96"',
        color: 'Clear',
        weight: 15,
        basePrice: 35.0,
        markupFactor: 2.5,
        createdAt: Timestamp.now(),
      },
      {
        name: 'White Acrylic',
        categoryId: categoryRefs[1],
        thickness: '1/8"',
        size: '48" x 96"',
        color: 'White',
        weight: 15,
        basePrice: 38.0,
        markupFactor: 2.5,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Birch Plywood',
        categoryId: categoryRefs[2],
        thickness: '1/2"',
        size: '48" x 96"',
        color: 'Natural',
        weight: 45,
        basePrice: 55.0,
        markupFactor: 1.8,
        createdAt: Timestamp.now(),
      },
      {
        name: 'MDF Board',
        categoryId: categoryRefs[2],
        thickness: '3/4"',
        size: '48" x 96"',
        color: 'White',
        weight: 65,
        basePrice: 28.0,
        markupFactor: 1.7,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Aluminum Sheet',
        categoryId: categoryRefs[3],
        thickness: '1/16"',
        size: '48" x 96"',
        color: 'Silver',
        weight: 35,
        basePrice: 75.0,
        markupFactor: 2.3,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Steel Sheet',
        categoryId: categoryRefs[3],
        thickness: '16 gauge',
        size: '48" x 96"',
        color: 'Gray',
        weight: 120,
        basePrice: 85.0,
        markupFactor: 2.1,
        createdAt: Timestamp.now(),
      },
    ];

    await Promise.all(
      materials.map(async (mat) => {
        const matRef = doc(collection(db, 'materials'));
        await setDoc(matRef, mat);
      })
    );

    // Initialize Processes
    console.log('Creating processes...');
    const processes = [
      {
        name: 'CNC Cutting',
        description: 'Computer Numeric Control cutting service',
        costType: 'per_sqft',
        costPerSqft: 2.5,
        isActive: true,
        order: 1,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Laminating',
        description: 'Protective laminate coating',
        costType: 'per_sqft',
        costPerSqft: 1.75,
        isActive: true,
        order: 2,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Edge Banding',
        description: 'Professional edge finishing',
        costType: 'per_sqft',
        costPerSqft: 0.5,
        isActive: true,
        order: 3,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Digital Printing',
        description: 'High-quality digital printing',
        costType: 'per_sqft',
        costPerSqft: 3.0,
        isActive: true,
        order: 4,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Installation',
        description: 'Professional installation service',
        costType: 'flat_rate',
        flatCost: 150.0,
        isActive: true,
        order: 5,
        createdAt: Timestamp.now(),
      },
    ];

    await Promise.all(
      processes.map(async (proc) => {
        const procRef = doc(collection(db, 'processes'));
        await setDoc(procRef, proc);
      })
    );

    // Initialize Labor Rates
    console.log('Creating labor rates...');
    const laborRates = [
      {
        name: 'Standard Labor',
        description: 'Standard labor rate',
        rateType: 'hourly',
        hourlyRate: 45.0,
        markupFactor: 1.5,
        isActive: true,
        order: 1,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Premium Labor',
        description: 'Skilled labor at premium rates',
        rateType: 'hourly',
        hourlyRate: 65.0,
        markupFactor: 1.5,
        isActive: true,
        order: 2,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Installation Labor',
        description: 'On-site installation labor',
        rateType: 'hourly',
        hourlyRate: 55.0,
        markupFactor: 1.5,
        isActive: true,
        order: 3,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Design Service',
        description: 'Custom design service',
        rateType: 'hourly',
        hourlyRate: 75.0,
        markupFactor: 2.0,
        isActive: true,
        order: 4,
        createdAt: Timestamp.now(),
      },
    ];

    await Promise.all(
      laborRates.map(async (rate) => {
        const rateRef = doc(collection(db, 'laborRates'));
        await setDoc(rateRef, rate);
      })
    );

    // Initialize Vinyls
    console.log('Creating vinyls...');
    const vinyls = [
      {
        name: 'Cast Vinyl',
        color: 'White',
        type: 'Matte',
        basePrice: 45.0,
        markupFactor: 2.0,
        width: 54,
        isActive: true,
        order: 1,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Cast Vinyl',
        color: 'Black',
        type: 'Matte',
        basePrice: 48.0,
        markupFactor: 2.0,
        width: 54,
        isActive: true,
        order: 2,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Calendered Vinyl',
        color: 'Clear',
        type: 'Glossy',
        basePrice: 50.0,
        markupFactor: 2.0,
        width: 54,
        isActive: true,
        order: 3,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Premium Chrome',
        color: 'Silver',
        type: 'Mirror',
        basePrice: 65.0,
        markupFactor: 2.2,
        width: 54,
        isActive: true,
        order: 4,
        createdAt: Timestamp.now(),
      },
      {
        name: 'Brushed Aluminum',
        color: 'Silver',
        type: 'Matte',
        basePrice: 72.0,
        markupFactor: 2.3,
        width: 60,
        isActive: true,
        order: 5,
        createdAt: Timestamp.now(),
      },
    ];

    await Promise.all(
      vinyls.map(async (vinyl) => {
        const vinylRef = doc(collection(db, 'vinyls'));
        await setDoc(vinylRef, vinyl);
      })
    );

    // Initialize Company Settings
    console.log('Creating company settings...');
    await setDoc(doc(db, 'companySettings', 'default'), {
      id: 'default',
      branding: {
        companyName: 'Red Max Hub',
        logoUrl: '',
        logoAlt: 'Company Logo',
        faviconUrl: '',
        tagline: 'Professional Quoting Solutions',
      },
      colors: {
        primary: '#667eea',
        secondary: '#764ba2',
        accent: '#f59e0b',
        background: '#ffffff',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        border: '#e5e7eb',
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        headingSize: 24,
        bodySize: 14,
        fontScale: {
          h1: 32,
          h2: 24,
          h3: 20,
          body: 14,
        },
      },
      quotes: {
        pdfHeaderText: 'QUOTE',
        pdfFooterText: 'Thank you for your business!',
        quotePrefix: 'QT-',
        showMarkupDetails: false,
        currencySymbol: '$',
        defaultTerms: 'Net 30',
        notesTemplate: '',
        defaultTaxRate: 0,
      },
      email: {
        senderEmail: '',
        senderName: 'Red Max Hub',
        quoteSubjectLine: 'Your Quote from Red Max Hub',
        quoteEmailTemplate: 'Please see attached quote.',
      },
      updatedAt: Timestamp.now(),
    });

    console.log('✅ Firestore initialization complete!');
  } catch (error) {
    console.error('❌ Error initializing Firestore:', error);
    throw error;
  }
}
